import { useEffect, useId } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  accent?: string;        // CSS color (literal hex); default #f5c542
  icon?: React.ReactNode; // small icon-tile in the header
  footer?: React.ReactNode;
  children: React.ReactNode;
};

export function ModalShell({
  open, onClose, title, subtitle, accent = '#f5c542', icon, footer, children,
}: Props) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="modal-backdrop"
      onClick={onClose}
      style={{
        // Anchor to the DYNAMIC viewport (100dvh), not `inset:0`. On Telegram
        // Android the layout viewport extends below the visible WebView, so a
        // flex-end overlay using inset:0 pushes the sheet (and its footer/buy
        // button) off the bottom of the screen. 100dvh tracks the visible area.
        position: 'fixed', top: 0, left: 0, right: 0, height: '100dvh', zIndex: 300,
        background: 'radial-gradient(80% 70% at 50% 100%, rgba(5,8,18,0.92), rgba(5,8,18,0.7))',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal={true}
        aria-labelledby={titleId}
        className="cradle-app"
        style={{
          width: 'calc(100% - 16px)', maxWidth: 380, marginBottom: 0,
          borderRadius: 22, overflow: 'hidden',
          background: 'linear-gradient(180deg, #1d2942 0%, #0a0f1c 100%)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.10) inset, 0 20px 40px -10px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
          position: 'relative',
          // Override .cradle-app's min-height:100% (meant for the menu shell) —
          // it otherwise beats maxHeight and inflates the modal full-screen.
          minHeight: 0,
          maxHeight: '88dvh', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* drag-handle decoration */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
        </div>

        {/* header */}
        <div style={{
          padding: '14px 20px 14px',
          background: `radial-gradient(80% 60% at 50% 0%, ${accent}22 0%, transparent 80%)`,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'relative',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {icon && (
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: `linear-gradient(180deg, ${accent}33, ${accent}11)`,
              boxShadow: `inset 0 0 0 1px ${accent}66`,
              display: 'grid', placeItems: 'center', color: '#fff', flex: '0 0 auto',
            }}>
              {icon}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {subtitle && (
              <div style={{
                fontFamily: 'var(--f-display)', fontSize: 9, letterSpacing: '0.20em',
                color: 'var(--tx-faint)',
              }}>{subtitle}</div>
            )}
            <div
              id={titleId}
              style={{
                fontFamily: 'var(--f-display)', fontSize: 20, letterSpacing: '0.04em',
                marginTop: subtitle ? 2 : 0, color: '#fff',
                textShadow: `0 0 12px ${accent}55`,
              }}
            >{title}</div>
          </div>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            style={{
              appearance: 'none', border: 0, cursor: 'pointer',
              width: 28, height: 28, borderRadius: 999,
              background: 'rgba(255,255,255,0.05)', color: 'var(--tx-dim)',
              fontSize: 14,
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
              flex: '0 0 auto',
            }}
          >✕</button>
        </div>

        {/* body */}
        <div style={{
          padding: '14px 16px', overflowY: 'auto', flex: 1,
          touchAction: 'pan-y', overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
        }}>
          {children}
        </div>

        {/* footer */}
        {footer && (
          <div style={{ display: 'flex', gap: 8, padding: '14px 20px 22px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
